import React, { useState, useRef, useEffect, useCallback } from 'react';
import eventBus from '../core/EventBus';
import assistant from '../core/DesignerAssistant';
import speech from '../core/SpeechService';
import useIsMobile from '../hooks/useIsMobile';

// Simple markdown-like bold rendering
function renderAnswer(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-semibold text-gray-800">{part.slice(2, -2)}</strong>;
    }
    // Handle newlines
    const lines = part.split('\n');
    return lines.map((line, j) => (
      <React.Fragment key={`${i}-${j}`}>
        {j > 0 && <br />}
        {line}
      </React.Fragment>
    ));
  });
}

// ── Animated Mascot SVG ──
function Mascot({ size = 64, isWaving = false, className = '' }) {
  return (
    <div className={`relative ${className}`} style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" width={size} height={size}>
        {/* Body */}
        <ellipse cx="50" cy="62" rx="28" ry="30" fill="url(#mascotGrad)" />
        {/* Belly */}
        <ellipse cx="50" cy="68" rx="18" ry="18" fill="#e0e7ff" opacity="0.6" />
        {/* Head */}
        <circle cx="50" cy="32" r="22" fill="url(#mascotGrad)" />
        {/* Antenna */}
        <line x1="50" y1="10" x2="50" y2="4" stroke="#818cf8" strokeWidth="2.5" strokeLinecap="round" />
        <circle cx="50" cy="3" r="3" fill="#fbbf24" className="mascot-blink" style={{ animationDelay: '1s' }} />
        {/* Eyes */}
        <g className="mascot-blink">
          <ellipse cx="41" cy="30" rx="5" ry="5.5" fill="white" />
          <ellipse cx="59" cy="30" rx="5" ry="5.5" fill="white" />
          <circle cx="42" cy="29" r="2.5" fill="#1e1b4b" />
          <circle cx="60" cy="29" r="2.5" fill="#1e1b4b" />
          {/* Eye shine */}
          <circle cx="43" cy="28" r="1" fill="white" />
          <circle cx="61" cy="28" r="1" fill="white" />
        </g>
        {/* Mouth - happy smile */}
        <path d="M43 37 Q50 43 57 37" fill="none" stroke="#312e81" strokeWidth="2" strokeLinecap="round" />
        {/* Cheek blush */}
        <circle cx="35" cy="36" r="4" fill="#fca5a5" opacity="0.4" />
        <circle cx="65" cy="36" r="4" fill="#fca5a5" opacity="0.4" />
        {/* Left arm */}
        <ellipse cx="24" cy="60" rx="7" ry="10" fill="url(#mascotGrad)" transform="rotate(-15 24 60)" />
        {/* Right arm - waving */}
        <g className={isWaving ? 'mascot-wave' : ''} style={{ transformOrigin: '76px 55px' }}>
          <ellipse cx="76" cy="52" rx="7" ry="10" fill="url(#mascotGrad)" transform="rotate(15 76 52)" />
          {/* Hand */}
          <circle cx="78" cy="43" r="5" fill="#a5b4fc" />
        </g>
        {/* Feet */}
        <ellipse cx="38" cy="90" rx="10" ry="5" fill="#6366f1" />
        <ellipse cx="62" cy="90" rx="10" ry="5" fill="#6366f1" />
        {/* Gradient definition */}
        <defs>
          <linearGradient id="mascotGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#818cf8" />
            <stop offset="100%" stopColor="#6366f1" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}

// ── Speech bubble that appears next to mascot ──
function MascotBubble({ text, visible }) {
  if (!visible) return null;
  const isDark = document.documentElement.classList.contains('dark');
  return (
    <div
      className="mascot-speech-bubble absolute right-full mr-2 rounded-2xl shadow-xl px-4 py-3 text-[13px] leading-relaxed whitespace-normal font-semibold"
      style={{
        minWidth: 160,
        maxWidth: text.length > 80 ? 300 : 220,
        bottom: '100%',
        marginBottom: 8,
        ...(isDark ? {
          background: 'linear-gradient(135deg, #0a1628, #0f2440)',
          border: '1px solid rgba(0,255,136,0.4)',
          color: '#00ff88',
          boxShadow: '0 4px 24px rgba(0,255,136,0.2), 0 0 12px rgba(0,204,255,0.15)',
          textShadow: '0 0 8px rgba(0,255,136,0.3)',
        } : {
          background: 'linear-gradient(135deg, #fefce8, #fef3c7)',
          border: '1px solid #fde68a',
          color: '#92400e',
          boxShadow: '0 4px 20px rgba(251,191,36,0.25)',
        }),
      }}
    >
      {text}
      {/* Arrow pointing down-right */}
      <div
        className="absolute -bottom-[6px] right-4 w-3 h-3 transform rotate-45"
        style={{
          ...(isDark ? {
            background: '#0f2440',
            borderRight: '1px solid rgba(0,255,136,0.4)',
            borderBottom: '1px solid rgba(0,255,136,0.4)',
          } : {
            background: '#fef3c7',
            borderRight: '1px solid #fde68a',
            borderBottom: '1px solid #fde68a',
          }),
        }}
      />
    </div>
  );
}

const DEFAULT_CHIPS = [
  { id: 'add-text', label: 'Text einfügen' },
  { id: 'add-shapes', label: 'Formen' },
  { id: 'bleed', label: 'Beschnitt' },
  { id: 'finishing', label: 'Verarbeitung' },
  { id: 'shortcuts', label: 'Tastenkürzel' },
];

const MASCOT_HINTS = [
  'Hey! Füge Text hinzu und passe Schriftart & Farbe an!',
  'Psst! Du kannst mir auch per Sprache Fragen stellen!',
  'Probier mal eine Foto-Collage aus – sieht super aus!',
  'Lenny-Tipp: Mit Strg+Z machst du Änderungen rückgängig!',
  'Wusstest du? Du kannst eigene Bilder hochladen!',
  'Lenny-Tipp: Nutze die Vorschau um dein Design zu prüfen!',
  'Hey! Brauchst du Hilfe? Klick mich einfach an!',
  'Lenny-Tipp: Objekte lassen sich an Kanten ausrichten!',
  'Du kannst QR-Codes direkt im Designer erstellen!',
  'Lenny-Tipp: Ziehe Cliparts per Drag & Drop auf die Leinwand!',
  'Hey! Hast du schon die Formen-Bibliothek entdeckt?',
  'Lenny-Tipp: Doppelklick auf Text zum schnellen Bearbeiten!',
  'Nicht vergessen: Design speichern vor dem Schließen!',
  'Wow, über 80 Schriftarten stehen dir zur Verfügung!',
  'Lenny-Tipp: Gruppiere Objekte mit Strg+G!',
];

// Live tour actions need canvasManager — passed via ref from the component
const buildQuickTourSteps = (canvasManager) => [
  { selector: '[data-guide-id="panel-dimensions"]', label: '1. Maße festlegen', speech: 'Schritt 1: Hier stellst du Breite und Höhe deines Banners ein — in Zentimetern.', openPanel: null },
  { selector: '[data-guide-id="btn-finishing"]', label: '2. Verarbeitung wählen', speech: 'Schritt 2: Wähle zuerst die Verarbeitung — Ösen, Saum, Hohlsaum oder sauberer Schnitt. So weißt du gleich, wie dein Banner befestigt wird.', openPanel: 'finishing' },
  { selector: '[data-guide-id="tool-bgcolor"]', label: '3. Hintergrundfarbe', speech: 'Schritt 3: Jetzt eine Hintergrundfarbe wählen. Schau mal, ich setze dir ein schönes Blau!', openPanel: 'bgcolor', action: (cm) => { if (cm) { speech.mute(); cm.setBackgroundColor('#3b82f6'); speech.unmute(); } } },
  { selector: '[data-guide-id="tool-bgimage"]', label: '4. Oder ein Hintergrundbild', speech: 'Alternativ kannst du auch ein eigenes Hintergrundbild hochladen oder aus tausenden Fotos wählen.', openPanel: 'bgimage' },
  { selector: '[data-guide-id="tool-text"]', label: '5. Text hinzufügen', speech: 'Schritt 4: Füge Texte hinzu! Ich schreibe mal eine Beispiel-Überschrift für dich.', openPanel: 'text', action: (cm) => { if (cm) { speech.mute(); cm.addText('Alles Gute!', { fontSize: Math.round(72 * cm.getObjScale()), fontFamily: 'Pacifico', fill: '#ffffff', fontWeight: 'bold', name: 'Tour-Beispiel' }); speech.unmute(); } } },
  { selector: '[data-guide-id="tool-image"]', label: '6. Eigene Bilder', speech: 'Schritt 5: Lade eigene Fotos hoch, zum Beispiel ein Portraitbild oder Logo.', openPanel: 'pixabay' },
  { selector: '[data-guide-id="tool-cliparts"]', label: '7. Cliparts & Deko', speech: 'Schritt 6: Dekoriere mit Cliparts — einfach anklicken oder per Drag and Drop auf die Leinwand ziehen.', openPanel: 'cliparts' },
  { selector: '[data-guide-id="tool-shapes"]', label: '8. Formen & Rahmen', speech: 'Hier findest du Formen wie Kreise, Sterne und Rahmen für dein Design.', openPanel: 'shapes' },
  { selector: '[data-guide-id="btn-preview"]', label: '9. Vorschau prüfen', speech: 'Schritt 7: Prüfe dein fertiges Design in der Vorschau — genau so wird es gedruckt.', openPanel: null },
  { selector: '[data-guide-id="btn-save"]', label: '10. Speichern & bestellen', speech: 'Schritt 8: Speichere dein Design und lege es in den Warenkorb. In nur 2 Minuten fertig gestaltet!', openPanel: null },
];

function AssistantPanel({ whatsappNumber, whatsappText, canvasManager }) {
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [tourActive, setTourActive] = useState(false);
  const [tourStep, setTourStep] = useState(-1);
  const tourTimerRef = useRef(null);
  const tourStepsRef = useRef(buildQuickTourSteps(canvasManager));
  // Keep steps up-to-date with canvasManager
  useEffect(() => { tourStepsRef.current = buildQuickTourSteps(canvasManager); }, [canvasManager]);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: 'Servus! Ich bin Lenny, dein Designer-Assistent. Frag mich alles rund um deine Gestaltung – zum Beispiel "Wie füge ich Text ein?" oder "Was ist Beschnitt?" Du kannst auch das Mikrofon nutzen, um per Sprache zu fragen!',
    },
  ]);
  const [input, setInput] = useState('');
  const [chips, setChips] = useState(DEFAULT_CHIPS);
  const [isTyping, setIsTyping] = useState(false);
  const [isWaving, setIsWaving] = useState(true);
  const [showBubble, setShowBubble] = useState(false);
  const [bubbleText, setBubbleText] = useState('');
  const [isRecording, setIsRecording] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const recognitionRef = useRef(null);
  const bubbleTimerRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  // Listen for toggle from toolbar button
  useEffect(() => {
    const unsub = eventBus.on('assistant:toggle', () => {
      setIsOpen(prev => !prev);
    });
    return unsub;
  }, []);

  // Lenny intro + periodic hints
  const introPlayedRef = useRef(false);
  useEffect(() => {
    // Intro: Lenny stellt sich vor (einmalig)
    const introText = 'Servus! Mein Name ist Lenny und ich helfe dir gerne rund um deine individuelle Gestaltung. Frag mich per Sprache oder per Text — ich kenn mich bestens im Selenny®-Designer aus!';
    const introTimer = setTimeout(() => {
      if (!introPlayedRef.current) {
        introPlayedRef.current = true;
        setBubbleText(introText);
        setShowBubble(true);
        setIsWaving(true);
        // Intro-Bubble nicht vorlesen — Begruessung kommt separat via welcome()
        setTimeout(() => {
          setShowBubble(false);
          setIsWaving(false);
        }, 10000);
      }
    }, 2000);

    // Stop waving after initial animation if intro hasn't fired yet
    const waveTimer = setTimeout(() => { if (!introPlayedRef.current) setIsWaving(false); }, 2000);

    // Show random speech bubbles periodically when panel is closed
    const startBubbleLoop = () => {
      const show = () => {
        if (!isOpen) {
          const hint = MASCOT_HINTS[Math.floor(Math.random() * MASCOT_HINTS.length)];
          setBubbleText(hint);
          setShowBubble(true);
          setIsWaving(true);
          setTimeout(() => {
            setShowBubble(false);
            setIsWaving(false);
          }, 6000);
        }
      };
      // First hint after intro (18s), then every 15 seconds
      bubbleTimerRef.current = setTimeout(() => {
        show();
        bubbleTimerRef.current = setInterval(show, 15000);
      }, 18000);
    };
    startBubbleLoop();

    return () => {
      clearTimeout(introTimer);
      clearTimeout(waveTimer);
      if (bubbleTimerRef.current) {
        clearTimeout(bubbleTimerRef.current);
        clearInterval(bubbleTimerRef.current);
      }
    };
  }, []);

  // Hide bubble when panel opens
  useEffect(() => {
    if (isOpen) {
      setShowBubble(false);
    }
  }, [isOpen]);

  // Quick tour step progression
  useEffect(() => {
    if (!tourActive || tourStep < 0) return;
    const steps = tourStepsRef.current;
    if (tourStep >= steps.length) {
      // Tour finished — close all panels
      eventBus.emit('toolbar:close-dialogs');
      setTourActive(false);
      setTourStep(-1);
      setBubbleText('Fertig! Du bist bereit, dein Banner zu gestalten!');
      setShowBubble(true);
      setIsWaving(true);
      // Tour-Ende nicht vorlesen
      setTimeout(() => { setShowBubble(false); setIsWaving(false); }, 6000);
      return;
    }
    const step = steps[tourStep];
    // Close any previously opened panel first
    eventBus.emit('toolbar:close-dialogs');
    // Open the panel for this step (after a short delay so close finishes)
    const openTimer = setTimeout(() => {
      if (step.openPanel) {
        eventBus.emit('assistant:open-panel', { panel: step.openPanel });
      }
      // Execute live action (e.g., set color, add text)
      if (step.action) {
        setTimeout(() => step.action(canvasManager), 400);
      }
    }, 200);
    // Show spotlight
    setTimeout(() => {
      eventBus.emit('assistant:spotlight', { selector: step.selector, label: step.label });
    }, 300);
    // Show bubble
    setBubbleText(step.speech);
    setShowBubble(true);
    setIsWaving(true);
    // Tour-Steps nicht vorlesen — User liest die Bubble
    if ('speechSynthesis' in window) speechSynthesis.cancel();
    // Dynamic duration: ~100ms per character, minimum 6s, maximum 12s
    const duration = Math.max(6000, Math.min(12000, step.speech.length * 100));
    tourTimerRef.current = setTimeout(() => {
      setShowBubble(false);
      setIsWaving(false);
      setTimeout(() => setTourStep(s => s + 1), 500);
    }, duration);
    return () => {
      clearTimeout(openTimer);
      if (tourTimerRef.current) clearTimeout(tourTimerRef.current);
      // Stop speech when moving to next step
      if ('speechSynthesis' in window) speechSynthesis.cancel();
    };
  }, [tourActive, tourStep]);

  const startQuickTour = useCallback(() => {
    setIsOpen(false);
    setShowBubble(false);
    setTourActive(true);
    setTourStep(0);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        const btn = document.getElementById('assistant-toggle-btn');
        if (btn && btn.contains(e.target)) return;
        setIsOpen(false);
      }
    };
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClick);
    }, 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [isOpen]);

  // Initialize Speech Recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.lang = 'de-DE';
      recognition.continuous = false;
      recognition.interimResults = true;

      recognition.onresult = (event) => {
        let transcript = '';
        for (let i = 0; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        setInput(transcript);
        // If the result is final, auto-send
        if (event.results[event.results.length - 1].isFinal) {
          setTimeout(() => {
            setIsRecording(false);
            if (transcript.trim()) {
              handleSendDirect(transcript.trim());
            }
          }, 300);
        }
      };

      recognition.onerror = () => {
        setIsRecording(false);
      };

      recognition.onend = () => {
        setIsRecording(false);
      };

      recognitionRef.current = recognition;
    }
  }, []);

  const toggleRecording = useCallback(() => {
    if (!recognitionRef.current) {
      // Fallback message if Speech API not available
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: 'Spracheingabe wird von deinem Browser leider nicht unterstützt. Bitte verwende Chrome oder Edge für diese Funktion.',
      }]);
      return;
    }

    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    } else {
      setInput('');
      setIsRecording(true);
      recognitionRef.current.start();
    }
  }, [isRecording]);

  const executeActions = useCallback((actions) => {
    if (!actions || actions.length === 0) return;

    setMessages((prev) => [
      ...prev,
      { role: 'assistant', text: 'Ich zeige dir wo...', isGuide: true },
    ]);

    let delay = 600;
    actions.forEach((action) => {
      setTimeout(() => {
        switch (action.type) {
          case 'highlight':
            eventBus.emit('assistant:highlight', { selector: action.selector });
            break;
          case 'spotlight':
            eventBus.emit('assistant:spotlight', {
              selector: action.selector,
              label: action.label || '',
            });
            break;
          case 'open':
            eventBus.emit('assistant:open-panel', { panel: action.panel });
            break;
          case 'click':
            const el = document.querySelector(action.selector);
            if (el) el.click();
            break;
          default:
            break;
        }
      }, delay);
      delay += 800;
    });
  }, []);

  // Direct send (used by speech recognition)
  const handleSendDirect = useCallback(
    (text) => {
      const query = text.trim();
      if (!query) return;

      setMessages((prev) => [...prev, { role: 'user', text: query }]);
      setInput('');
      setIsTyping(true);

      setTimeout(() => {
        const result = assistant.match(query);
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', text: result.answer, actions: result.actions },
        ]);
        setIsTyping(false);

        // Antworten werden nicht vorgelesen — User liest schneller als TTS

        if (result.related && result.related.length > 0) {
          const relatedChips = result.related.map((rid) => ({
            id: rid,
            label: assistant.getLabel(rid),
          }));
          setChips(relatedChips);
        }

        if (result.actions && result.actions.length > 0) {
          executeActions(result.actions);
        }
      }, 400 + Math.random() * 300);
    },
    [executeActions]
  );

  const handleSend = useCallback(
    (text) => {
      handleSendDirect(text || input);
    },
    [input, handleSendDirect]
  );

  const handleChipClick = useCallback(
    (chipId) => {
      const result = assistant.getById(chipId);
      if (!result) return;

      const label = assistant.getLabel(chipId);
      setMessages((prev) => [...prev, { role: 'user', text: label }]);
      setIsTyping(true);

      setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', text: result.answer, actions: result.actions },
        ]);
        setIsTyping(false);

        if (result.related && result.related.length > 0) {
          const relatedChips = result.related.map((rid) => ({
            id: rid,
            label: assistant.getLabel(rid),
          }));
          setChips(relatedChips);
        }

        if (result.actions && result.actions.length > 0) {
          executeActions(result.actions);
        }
      }, 300);
    },
    [executeActions]
  );

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating mascot + buttons - bottom-right */}
      {!isOpen && (
        <div className={`fixed flex flex-col items-end gap-2 ${isMobile ? 'z-[99] right-3' : 'z-[400] right-5'} ${!isMobile ? (new URLSearchParams(window.location.search).get('embedded') === '1' ? 'bottom-5' : 'bottom-14') : ''}`} style={isMobile ? { bottom: 'calc(180px + env(safe-area-inset-bottom, 0px))', animation: 'assistant-float-in 0.6s cubic-bezier(0.16,1,0.3,1) both 1.5s' } : { animation: 'assistant-float-in 0.6s cubic-bezier(0.16,1,0.3,1) both 1.5s' }}>
          {/* Speech bubble */}
          <MascotBubble text={bubbleText} visible={showBubble} />

          {/* Mascot + Lenny hilft Button */}
          <button
            onClick={() => { setIsOpen(true); setShowBubble(false); }}
            className="relative flex items-end gap-0 group cursor-pointer bg-transparent border-none p-0"
          >
            <div className="mascot-float relative -mr-2 mb-1 z-10">
              <Mascot size={isMobile ? 40 : 56} isWaving={isWaving} />
            </div>
            <div className={`flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg group-hover:shadow-xl transition-all group-hover:scale-105 group-active:scale-95 ${isMobile ? 'pl-4 pr-3 py-1.5' : 'pl-5 pr-4 py-2.5'}`}>
              <span className={`font-semibold whitespace-nowrap ${isMobile ? 'text-xs' : 'text-sm'}`}>Lenny hilft!</span>
              <svg className="w-4 h-4 opacity-70" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 18h.01M12 14a2 2 0 0 0 .5-3.9A2 2 0 0 1 12 6a2 2 0 0 1 2 2" />
                <circle cx="12" cy="12" r="10" />
              </svg>
            </div>
            {messages.length <= 1 && (
              <span className="absolute -top-1 right-0 w-3.5 h-3.5 bg-red-500 rounded-full border-2 border-white animate-pulse" />
            )}
          </button>

          {/* Quick tour + WhatsApp row */}
          <div className="flex items-center gap-2">
            {!tourActive && (
              <button
                onClick={startQuickTour}
                className={`flex items-center gap-1.5 rounded-full font-semibold transition-all hover:scale-105 active:scale-95 ${isMobile ? 'px-2.5 py-1 text-[10px]' : 'px-3 py-1.5 text-[11px]'}`}
                style={{ background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)', color: '#fff', boxShadow: '0 2px 8px rgba(139,92,246,0.35)' }}
              >
                <svg className={isMobile ? 'w-3 h-3' : 'w-3.5 h-3.5'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                2 Min Schnelleinführung
              </button>
            )}
            {whatsappNumber && (
              <a
                href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappText || '')}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp Chat"
                title="WhatsApp Chat"
                style={{
                  width: isMobile ? 32 : 38, height: isMobile ? 32 : 38, borderRadius: '50%', background: '#25D366',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 2px 10px rgba(37,211,102,0.35)',
                  transition: 'transform 0.2s ease',
                  textDecoration: 'none', flexShrink: 0,
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </a>
            )}
          </div>
        </div>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div
          ref={panelRef}
          className={`fixed z-[500] bg-white shadow-2xl border border-gray-200/80 flex flex-col overflow-hidden animate-assistant-panel-in ${
            isMobile
              ? 'inset-0 rounded-none'
              : 'bottom-4 right-5 w-[370px] h-[500px] rounded-2xl'
          }`}
        >
          {/* Header with mini mascot */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center overflow-hidden p-0.5">
                <Mascot size={30} />
              </div>
              <div>
                <p className="text-sm font-semibold leading-tight">Lenny — Designer-Assistent</p>
                <p className="text-[10px] text-white/70 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full inline-block" />
                  Immer bereit zu helfen
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="w-7 h-7 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-assistant-msg-in`}
              >
                {msg.role === 'assistant' && !msg.isGuide && (
                  <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center shrink-0 mt-1 mr-1.5">
                    <Mascot size={18} />
                  </div>
                )}
                <div
                  className={`max-w-[80%] rounded-xl px-3 py-2 text-[13px] leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-blue-500 text-white rounded-br-sm'
                      : msg.isGuide
                      ? 'bg-indigo-50 text-indigo-600 border border-indigo-100 rounded-bl-sm text-[12px] italic'
                      : 'bg-gray-100 text-gray-700 rounded-bl-sm'
                  }`}
                >
                  {msg.role === 'user' ? msg.text : renderAnswer(msg.text)}
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {isTyping && (
              <div className="flex justify-start animate-assistant-msg-in">
                <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center shrink-0 mt-1 mr-1.5">
                  <Mascot size={18} />
                </div>
                <div className="bg-gray-100 rounded-xl rounded-bl-sm px-4 py-2.5 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick chips */}
          {chips.length > 0 && (
            <div className="px-3 py-2 border-t border-gray-100 shrink-0">
              <div className="flex flex-wrap gap-1.5">
                {chips.map((chip) => (
                  <button
                    key={chip.id}
                    onClick={() => handleChipClick(chip.id)}
                    className="text-[11px] px-2.5 py-1 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors border border-blue-100 whitespace-nowrap"
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input area with speech button */}
          <div className="px-3 py-2.5 border-t border-gray-200 shrink-0">
            {/* Recording indicator */}
            {isRecording && (
              <div className="flex items-center gap-2 mb-2 px-2 py-1.5 bg-red-50 rounded-lg border border-red-100 text-[11px] text-red-600">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                Sprich jetzt... Ich höre zu!
              </div>
            )}
            <div className="flex items-center gap-2">
              {/* Microphone button */}
              <button
                onClick={toggleRecording}
                className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all shrink-0 ${
                  isRecording
                    ? 'bg-red-500 text-white mic-recording'
                    : 'bg-gray-100 text-gray-500 hover:bg-indigo-100 hover:text-indigo-600'
                }`}
                title={isRecording ? 'Aufnahme stoppen' : 'Per Sprache fragen'}
              >
                {isRecording ? (
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="6" y="6" width="12" height="12" rx="2" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                    <line x1="12" y1="19" x2="12" y2="23" />
                    <line x1="8" y1="23" x2="16" y2="23" />
                  </svg>
                )}
              </button>
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isRecording ? 'Ich höre zu...' : 'Frag mich etwas...'}
                className="flex-1 text-sm px-3 py-2 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-gray-50"
                disabled={isRecording}
              />
              <button
                onClick={() => handleSend()}
                disabled={!input.trim()}
                className="w-9 h-9 rounded-xl bg-blue-500 text-white flex items-center justify-center hover:bg-blue-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 2L11 13M22 2l-7 20-4-9-9-4z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AssistantPanel;
